// @flow

import * as React from "react";
import skrollTop from "skrolltop";
import isEqual from "lodash/isEqual";
import cx from "classnames";

import styles from "./FormSection.scss";
import {
  defaultFormPageContextValue,
  FormPageContext,
  type FormPageContextValueType
} from "../FormPage/FormPage";
import { disableScroll, enableScroll } from "../../utils/scroll";

type FormSectionProps = {
  children: React.Node,
  disabled: boolean,
  id: string,
  index: number,
  title: string,
  subtitle: ?string,
  validatedFields: string[],
  withSeparator: boolean,
  shouldScroll: boolean,
  pageContext: FormPageContextValueType,
  isTitleDisplayed: boolean,
  isCounted: boolean,
  customSectionStyles: string,
  withoutIndex: boolean
};

const buildSectionNumber = (index: number) => `${index + 1}.`;

class FormSection extends React.Component<FormSectionProps> {
  sectionRef = React.createRef();

  unsubscribeFromPage: ?() => void = null;

  componentDidMount() {
    const { id, pageContext, validatedFields, isCounted } = this.props;

    this.unsubscribeFromPage = pageContext.registerSection(id, validatedFields, isCounted);
  }

  componentDidUpdate(prevProps) {
    this.setValidatedFields(prevProps);
    this.scrollToNextSection(prevProps);
  }

  componentWillUnmount() {
    if (this.unsubscribeFromPage) {
      this.unsubscribeFromPage();
    }
  }

  scrollToNextSection = (prevProps: FormSectionProps) => {
    const shouldScroll = this.shouldScroll(prevProps);

    if (shouldScroll) {
      const sectionElement = this.sectionRef && this.sectionRef.current;

      if (sectionElement && sectionElement instanceof HTMLElement) {
        // make sure sectionOffset is calculated properly
        setTimeout(() => {
          disableScroll();

          const sectionOffset = sectionElement.offsetTop;
          const sectionHeight = sectionElement.offsetHeight;
          // navbar height is calculated depending on window width
          const navbarHeight = window.innerWidth < 768 ? 72 : 153;

          const scrollAmount = sectionOffset + sectionHeight - navbarHeight;

          skrollTop.scrollTo({
            to: scrollAmount,
            callback: () => {
              enableScroll();
            }
          });
        }, 0);
      }
    }
  };

  shouldScroll = (prevProps: FormSectionProps) => {
    const {
      pageContext: { sectionsValidities: prevSectionsValidities }
    } = prevProps;
    const {
      pageContext: { sectionsValidities, form },
      shouldScroll,
      id,
      validatedFields
    } = this.props;

    const isSectionTouched = validatedFields.some(fieldName => {
      const fieldState = form && form.getFieldState(fieldName);

      return !!fieldState && fieldState.visited;
    });

    const prevIsSectionValid = prevSectionsValidities[id];
    const isSectionValid = sectionsValidities[id];

    return !prevIsSectionValid && isSectionValid && shouldScroll && isSectionTouched;
  };

  setValidatedFields = (prevProps: FormSectionProps) => {
    const { validatedFields: previousValidatedFields } = prevProps;
    const {
      pageContext: { setValidatedFieldsForSection, validate },
      id,
      validatedFields
    } = this.props;

    if (!isEqual(validatedFields, previousValidatedFields)) {
      setValidatedFieldsForSection(id, validatedFields).then(validate);
    }
  };

  render() {
    const {
      children,
      disabled,
      index,
      title,
      subtitle,
      withSeparator,
      isTitleDisplayed,
      customSectionStyles,
      withoutIndex
    } = this.props;
    return (
      <section
        className={cx(
          styles.section,
          {
            [styles.disabled]: disabled,
            [styles.sectionWithSeparator]: withSeparator
          },
          customSectionStyles
        )}
        ref={this.sectionRef}
      >
        <header className={styles.header}>
          {isTitleDisplayed && (
            <>
              {!withoutIndex && <span className={styles.index}>{buildSectionNumber(index)}</span>}
              <div className={styles.titleContainer}>
                <div className={styles.title}>{title}</div>
                <div className={styles.subtitle}>{subtitle}</div>
              </div>
            </>
          )}
        </header>
        <div className={styles.contentContainer}>{children}</div>
      </section>
    );
  }
}

const FormSectionWrapper = (props: FormSectionProps) => (
  <FormPageContext.Consumer>
    {(pageContext: FormPageContextValueType) => (
      <FormSection {...props} pageContext={pageContext} />
    )}
  </FormPageContext.Consumer>
);

FormSectionWrapper.defaultProps = {
  children: null,
  disabled: false,
  id: "",
  index: 0,
  title: "",
  subtitle: "",
  validatedFields: [],
  withSeparator: false,
  shouldScroll: false,
  isTitleDisplayed: true,
  isCounted: true,
  withoutIndex: false,
  customSectionStyles: "",
  pageContext: defaultFormPageContextValue
};

export default FormSectionWrapper;
